import React, { Fragment, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header';
import { useFinanceiroPlataforma } from '../../../hooks/useFinanceiroPlataforma';
import ResumoTab from './tabs/ResumoTab';
import PorLigaTab from './tabs/PorLigaTab';
import TransacoesTab from './tabs/TransacoesTab';
import ReembolsosTab from './tabs/ReembolsosTab';
import ConciliacaoTab from './tabs/ConciliacaoTab';

// Hub admin-only de financeiro da plataforma — molde exato de
// pages/liga/financeiro/FinanceiroLigaHub.tsx (?tab= + replace: true), sem o gate de permissão
// por liga (a negação real é o AdminRoute + hasRole('ADMIN') no resolver — Decision Gate desta
// fase). Chama useFinanceiroPlataforma() uma única vez e repassa tudo por props para a aba
// ativa; nenhuma aba busca resumo/série/quebra própria do Resumo — só Por liga dispara suas
// próprias 3 queries de drill-down (fora do trio do hook). Transações e Reembolsos (Phase 5)
// têm cada uma seu próprio hook de busca (useTransacoesPlataforma/useFilaReembolsosPlataforma).
//
// 2026-08-18: o filtro de liga que ficava AQUI, no topo, foi removido e devolvido às duas abas
// que de fato o usavam (Por liga e Transações), cada uma dona do seu. Ele se apresentava como
// global mas não tinha efeito em 3 das 5 abas: as 4 queries de `useFinanceiroPlataforma` não
// aceitam `ligaId` (Resumo é sempre plataforma inteira), e nem ReembolsosTab nem ConciliacaoTab
// jamais o receberam — o combobox seguia preenchido nelas, convidando a ler os números como se
// estivessem filtrados.
//
// AC-13 (financeiro-conciliacao-iugu Phase 5): a aba Conciliação (F-6) agora É registrada aqui —
// importada, presente em TabId/TABS e despachada pelo branch abaixo, consumindo sua própria
// query GraphQL (useConciliacaoIugu, ConciliacaoGraphQL ADMIN-only). O esqueleto da F-8
// (tesouraria) continua deliberadamente ausente, pelo MESMO mecanismo de ausência-de-registro
// que ocultava Conciliação até esta fase (precedente SaldoSaquesTab,
// financeiro-dashboard-liga-torneio Phase 4) — não importado, não entra em TabId nem em TABS.
// Importar sem renderizar dispararia R-COH-DEAD-IMPORT no code review.

type TabId = 'resumo' | 'porliga' | 'transacoes' | 'reembolsos' | 'conciliacao';

const TABS: { id: TabId; label: string }[] = [
  { id: 'resumo', label: 'Resumo' },
  { id: 'porliga', label: 'Por liga' },
  { id: 'transacoes', label: 'Transações' },
  { id: 'reembolsos', label: 'Reembolsos' },
  { id: 'conciliacao', label: 'Conciliação' },
];

const FinanceiroAdminPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // ?tab= ausente ou desconhecido cai em Resumo, sem quebrar.
  const activeTab: TabId = TABS.some((tab) => tab.id === tabParam) ? (tabParam as TabId) : 'resumo';
  const irParaAba = (id: TabId) => setSearchParams({ tab: id }, { replace: true });

  const {
    periodo,
    resumo,
    serie,
    quebraPorMeioPagamento,
    campanhas,
    loading,
    error,
    resumoIndisponivel,
    refetch,
  } = useFinanceiroPlataforma();

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />

        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            {/* Largura do conteúdo ampliada de 800px para 1200px (2026-08-18). São DOIS limites
                encadeados, não um: o tema aplica max-width 800px em `.middle-wrap` e 960px no pai
                `.middle-sidebar-left` -- soltar só o interno não passaria de 960. As duas classes
                são do tema e usadas por dezenas de outras páginas, então o override é inline,
                AQUI, em vez de no CSS compartilhado: as tabelas largas do financeiro (Transações
                tem 10 colunas) precisam do espaço, o resto do app não pediu para mudar. */}
            <div className="middle-sidebar-left" style={{ maxWidth: 1200 }}>
              <div className="middle-wrap" style={{ maxWidth: 1200 }}>
                <div className="mb-3">
                  <h3 className="fw-700 mb-0">Financeiro da plataforma</h3>
                </div>

                <ul className="nav nav-tabs mb-3" role="tablist">
                  {TABS.map((tab) => (
                    <li className="nav-item" key={tab.id} role="presentation">
                      <button
                        type="button"
                        className={`nav-link${activeTab === tab.id ? ' active' : ''}`}
                        onClick={() => irParaAba(tab.id)}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {activeTab === 'resumo' && (
                  <ResumoTab
                    resumo={resumo}
                    serie={serie}
                    quebraPorMeioPagamento={quebraPorMeioPagamento}
                    campanhas={campanhas}
                    loading={loading}
                    error={error}
                    resumoIndisponivel={resumoIndisponivel}
                    onRetry={refetch}
                    periodo={periodo}
                  />
                )}
                {activeTab === 'porliga' && <PorLigaTab periodo={periodo} />}
                {activeTab === 'transacoes' && <TransacoesTab periodo={periodo} />}
                {activeTab === 'reembolsos' && <ReembolsosTab periodo={periodo} />}
                {activeTab === 'conciliacao' && <ConciliacaoTab />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default FinanceiroAdminPage;
